import { hot } from "react-hot-loader/root";
import React from "react";
import {
  Banner,
  Button,
  Column,
  Columns,
  LabelValue,
  TextField,
  useCopyToClipboard,
} from "@sparkpost/matchbox";
import { Add, Help } from "@sparkpost/matchbox-icons";
import Section from "../components/Section";
import { i18n } from "../utils/utils";
import { createDomain, verifyRecords } from "../utils/ajax";
import { useAppState } from "../utils/context";

const DNS = () => {
  const { fields, updateField, setLoading, displayNotice } = useAppState();
  const { copy } = useCopyToClipboard();

  if (!fields.license_activated) {
    return (
      <Section>
        <Banner size="small" status="warning" onDismiss={() => {}}>
          {i18n("Text.NoDNSReason")}
        </Banner>
      </Section>
    );
  }

  const onCreateClick = async (e) => {
    setLoading(true);
    const result = await createDomain(fields.sending_domain);

    if (result.message) {
      const status = result.error
        ? "error"
        : result.success
        ? "success"
        : "warning";

      displayNotice(result.message, status);
    }

    if (result.fields) {
      if (result.fields.sending_domain) {
        updateField("sending_domain", result.fields.sending_domain);
      }

      if (result.fields.current_sending_domain) {
        updateField(
          "current_sending_domain",
          result.fields.current_sending_domain
        );
      }

      if (
        result.fields.sending_domain_record_hostname &&
        result.fields.sending_domain_record_value
      ) {
        updateField(
          "sending_domain_record_hostname",
          result.fields.sending_domain_record_hostname
        );

        updateField(
          "sending_domain_record_value",
          result.fields.sending_domain_record_value
        );

        updateField("domain_dkim_record_is_valid", null);
        updateField("domain_dkim_record_verified", null);
      }
    }

    setLoading(false);
  };

  const onVerifyClick = async (e) => {
    setLoading(true);
    const result = await verifyRecords(fields.sending_domain);

    if (result.message) {
      const status = result.error
        ? "error"
        : !!result.fields.domain_dkim_record_is_valid
        ? "success"
        : "warning";
      displayNotice(result.message, status);
    }

    if (result.fields) {
      if (typeof result.fields.domain_dkim_record_is_valid !== "undefined") {
        updateField(
          "domain_dkim_record_is_valid",
          result.fields.domain_dkim_record_is_valid
        );
      }

      if (typeof result.fields.domain_dkim_record_verified !== "undefined") {
        updateField(
          "domain_dkim_record_verified",
          result.fields.domain_dkim_record_verified
        );
      }

      if (typeof result.fields.sender_mail !== "undefined") {
        updateField("sender_mail", result.fields.sender_mail);
        updateField("sender_mail_prefix", result.fields.sender_mail_prefix);
      }
    }

    setLoading(false);
  };

  const onCopyClick = (text) => {
    copy(text);
    displayNotice(i18n("Text.TextCopied"), "success");
  };

  const hasDomainRecord =
    !!fields.sending_domain_record_hostname &&
    !!fields.sending_domain_record_value;

  const verificationExists = parseInt(fields.domain_dkim_record_verified) > 0;

  let sendingDomain = fields.sending_domain || "";

  if (sendingDomain === fields.default_sending_domain) {
    sendingDomain = "";
  }

  return (
    <>
      <Section
        title={i18n("Panels.DNS.SendingDomainField")}
        className="section-create-domain"
      >
        <div className="restricted-width">
          <TextField
            id="sending_domain"
            label={i18n("Fields.DNS.SendingDomain")}
            placeholder="your-domain.com"
            name="sending_domain"
            value={sendingDomain}
            onChange={(e) => {
              updateField(e.target.name, e.target.value);
            }}
            connectRight={
              <Button
                size="default"
                variant="outline"
                color="blue"
                onClick={onCreateClick}
                disabled={
                  fields.sending_domain === fields.current_sending_domain
                }
              >
                <Button.Icon as={Add} size={20} mr="200"></Button.Icon>
                {i18n("Button.CreateDomain")}
              </Button>
            }
          />
        </div>
      </Section>
      {hasDomainRecord && (
        <>
          <Section
            title={i18n("Panels.DNS.SendingDomainRecords")}
            className="section-domain-records"
          >
            <Columns>
              <Column className="dkim-record-hostname" width={1 / 3}>
                <LabelValue orientation="vertical">
                  <LabelValue.Label>
                    {i18n("Text.Hostname")}{" "}
                    <span
                      className="copy-link"
                      onClick={() => {
                        onCopyClick(fields.sending_domain_record_hostname);
                      }}
                    >
                      {i18n("Text.Copy")}
                    </span>
                  </LabelValue.Label>
                  <LabelValue.Value>
                    <code className="dkim-record-hostname">
                      {fields.sending_domain_record_hostname}
                    </code>
                  </LabelValue.Value>
                </LabelValue>
              </Column>
              <Column className="dkim-record-value">
                <LabelValue orientation="vertical">
                  <LabelValue.Label>
                    {i18n("Text.Value")}{" "}
                    <span
                      className="copy-link"
                      onClick={() => {
                        onCopyClick(fields.sending_domain_record_value);
                      }}
                    >
                      {i18n("Text.Copy")}
                    </span>
                  </LabelValue.Label>
                  <LabelValue.Value>
                    <code className="dkim-record-value">
                      {fields.sending_domain_record_value}
                    </code>
                  </LabelValue.Value>
                </LabelValue>
              </Column>
            </Columns>
          </Section>
          <Section
            title={i18n("Panels.DNS.SendingDomainVerify")}
            className="section-verify-domain"
          >
            {verificationExists && !fields.domain_dkim_record_is_valid && (
              <Banner
                mb="300"
                size="small"
                status="warning"
                onDismiss={() => {}}
              >
                {i18n("Fields.Verify.PropagationWarning")}
              </Banner>
            )}
            <Columns>
              <Column width="content">
                <Button
                  variant="outline"
                  color="blue"
                  disabled={!hasDomainRecord}
                  onClick={onVerifyClick}
                >
                  {i18n("Button.Verify")} {fields.sending_domain}
                </Button>
              </Column>
              <Column className="vertical-align">
                {verificationExists && (
                  <>
                    {fields.domain_dkim_record_is_valid ? (
                      <p className="last-verification-status valid">
                        <span className="icon dashicons dashicons-yes-alt"></span>
                        <span className="text">
                          {i18n("Text.DkimValid")}{" "}
                          <small>({fields.domain_dkim_record_verified})</small>
                        </span>
                      </p>
                    ) : (
                      <p className="last-verification-status invalid">
                        <span className="icon dashicons dashicons-no"></span>
                        <span className="text">
                          {i18n("Text.DkimInvalid")}{" "}
                          <small>({fields.domain_dkim_record_verified})</small>
                        </span>
                      </p>
                    )}
                  </>
                )}
              </Column>
            </Columns>
          </Section>
          <p className="help-link">
            <a
              href="https://docs.wpmailpro.com/docs/configure-sending-domain/"
              target="_blank"
            >
              <Help size={18} /> {i18n("Text.NeedHelp")}
            </a>
          </p>
        </>
      )}
    </>
  );
};

export default hot(DNS);
